import * as THREE from "three";

/** Locally owned 3D reward geometry; imported unit meshes never enter disposal. */
export class StarCeremonyEffect {
  readonly group = new THREE.Group();
  readonly stars: THREE.Mesh[] = [];
  readonly height: number;
  private readonly starGeometry: THREE.ExtrudeGeometry;
  private readonly gold = new THREE.MeshStandardMaterial({
    color: 0xedbd55,
    emissive: 0xb77616,
    emissiveIntensity: 0.3,
    metalness: 0.65,
    roughness: 0.3,
  });
  private readonly edge = new THREE.MeshStandardMaterial({
    color: 0xa9681d,
    metalness: 0.65,
    roughness: 0.35,
  });
  private readonly ringMaterial = new THREE.MeshStandardMaterial({
    color: 0xffd174,
    emissive: 0xffbc46,
    emissiveIntensity: 0.8,
    transparent: true,
    opacity: 0.7,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  private readonly ring: THREE.Mesh<THREE.RingGeometry>;
  private readonly particles: THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial>;
  private elapsed = 0;
  private disposed = false;
  private completed = false;

  constructor(count: number, modelHeight: number) {
    if (!Number.isInteger(count) || count < 1 || count > 5)
      throw new Error("A star ceremony requires 1–5 authoritative stars");
    this.height = modelHeight + 0.67;
    const shape = new THREE.Shape();
    for (let i = 0; i < 10; i++) {
      const angle = Math.PI / 2 + (i * Math.PI) / 5;
      const radius = i % 2 === 0 ? 1 : 0.46;
      const x = Math.cos(angle) * radius,
        y = Math.sin(angle) * radius;
      if (i === 0) shape.moveTo(x, y);
      else shape.lineTo(x, y);
    }
    shape.closePath();
    this.starGeometry = new THREE.ExtrudeGeometry(shape, {
      depth: 0.16,
      bevelEnabled: true,
      bevelSegments: 2,
      steps: 1,
      bevelSize: 0.07,
      bevelThickness: 0.05,
      curveSegments: 1,
    });
    this.starGeometry.translate(0, 0, -0.08);
    for (let i = 0; i < count; i++) {
      const star = new THREE.Mesh(this.starGeometry, [this.gold, this.edge]);
      star.name = `earned-star-${i + 1}`;
      star.scale.setScalar(0.19);
      star.castShadow = true;
      this.stars.push(star);
      this.group.add(star);
    }
    this.ring = new THREE.Mesh(new THREE.RingGeometry(1.13, 1.19, 64), this.ringMaterial);
    this.ring.rotation.x = -Math.PI / 2;
    this.ring.position.y = 0.045;
    this.group.add(this.ring);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(36 * 3), 3));
    this.particles = new THREE.Points(
      geometry,
      new THREE.PointsMaterial({
        color: 0xffd686,
        size: 0.035,
        transparent: true,
        opacity: 0.75,
        depthWrite: false,
      }),
    );
    this.group.add(this.particles);
    this.apply(0);
  }

  get done(): boolean {
    return this.completed;
  }

  update(dt: number): boolean {
    if (this.disposed || this.completed) return this.completed;
    this.elapsed = Math.min(3.2, this.elapsed + Math.max(0, dt));
    this.apply(this.elapsed / 3.2);
    return this.completed;
  }

  finish(): void {
    if (!this.disposed) this.apply(1);
  }

  private apply(progress: number): void {
    this.completed = progress >= 1;
    this.stars.forEach((star, i) => {
      const p = THREE.MathUtils.clamp((progress - i * 0.065) / 0.6, 0, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      const targetX = (i - (this.stars.length - 1) / 2) * 0.43;
      star.position.set(
        targetX * ease + Math.sin(i * 2.4) * (1 - ease),
        0.15 + (this.height - 0.33 - 0.15) * ease,
        0.22 + (1 - ease) * 0.45,
      );
      star.rotation.set(0, (1 - ease) * Math.PI * 1.5, (1 - ease) * 0.3);
      star.scale.setScalar(0.19 * (0.35 + 0.65 * ease));
    });
    this.ring.scale.setScalar(0.7 + Math.min(1, progress * 2) * 0.3);
    this.ringMaterial.opacity = this.completed ? 0.32 : 0.45 + Math.sin(progress * Math.PI) * 0.3;
    const positions = this.particles.geometry.getAttribute("position") as THREE.BufferAttribute;
    for (let i = 0; i < positions.count; i++) {
      const angle = i * 2.39996 + progress * 2;
      const fraction = (i % 9) / 9;
      positions.setXYZ(
        i,
        Math.cos(angle) * (0.6 + fraction * 0.55),
        0.12 + ((progress * 1.3 + fraction) % 1) * this.height,
        Math.sin(angle) * (0.6 + fraction * 0.55),
      );
    }
    positions.needsUpdate = true;
    this.particles.visible = !this.completed;
    this.particles.material.opacity = Math.sin(progress * Math.PI) * 0.8;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.starGeometry.dispose();
    this.gold.dispose();
    this.edge.dispose();
    this.ring.geometry.dispose();
    this.ringMaterial.dispose();
    this.particles.geometry.dispose();
    this.particles.material.dispose();
    this.group.clear();
  }
}
